import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { apiFetch } from '@/lib/api/client';

/**
 * 전자결재 화면의 데이터 접근(백엔드 `menu/approval`).
 *
 * 기안 → 결재선(조직도에서 지정) → 상신 → 결재/합의/전결 → 완료/반려/회수. 결재함은 사람 중심으로
 * 대기/진행/완료/상신/임시를 나눠 읽는다. 처리 자격은 서버가 데이터로 판정하므로(내 차례가 아니면 403)
 * 프론트 버튼은 UX 일 뿐이다.
 */

export type DocStatus = 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'RECALLED' | 'CANCELLED';
export type LineType =
  | 'DRAFT'
  | 'REVIEW'
  | 'AGREEMENT'
  | 'PARALLEL_AGREEMENT'
  | 'APPROVAL'
  | 'REFERENCE'
  | 'EXECUTION';
/** `DISAGREED` 는 합의줄의 반려 — 화면 글씨는 「반려」로 같고, 문서를 되돌리지 않는 점만 다르다. */
export type LineDecision =
  | 'PENDING'
  | 'APPROVED'
  | 'AGREED'
  | 'DISAGREED'
  | 'REJECTED'
  | 'SKIPPED'
  | 'DELEGATED';
export type InboxBox = 'all' | 'inbox' | 'ongoing' | 'done' | 'submitted' | 'drafts' | 'reference';

export type ApprovalForm = {
  id: string;
  formKey: string;
  name: string;
  category: string | null;
  titleTemplate: string | null;
  bodyGuide: string | null;
  amountUsed: boolean;
  /**
   * 새 기안을 열 수 있는가 — 오픈 게이트의 서식 키(V467). 옛 서버는 이 칸이 없어 선택 칸으로
   * 받고, 없으면 닫힌 것으로 본다(`isFormEnabled`).
   */
  draftOpen?: boolean;
};

export type OrgMember = {
  userId: string;
  name: string;
  rank: string | null;
  rankOrder: number | null;
  position: string | null;
  teamLead: boolean;
};
export type OrgUnit = { unitType: 'DEPARTMENT' | 'SITE'; unitName: string; members: OrgMember[] };

export type LineInput = {
  lineType: LineType;
  approverUserId: string;
  approverName: string;
  approverPosition: string | null;
  approverOrg: string | null;
  isFinal: boolean;
  /**
   * 숨김 참조 — 참조 효력은 그대로이고 <b>기안문에만 이름이 안 찍힌다.</b> 참조가 아닌 줄에서는 의미가 없다.
   * 값이 없으면 보통 참조다(옛 문서·저장한 결재선에는 이 칸이 아예 없다).
   */
  hidden?: boolean;
  /**
   * 참조 묶음 이름(V396) — [부서 전체]로 넣은 참조 줄에 그 부서·현장 이름이 적힌다.
   * 줄은 여전히 사람 하나이고, 화면과 기안문 「참 조:」 줄이 같은 이름을 하나로 접는다({@code referenceEntries}).
   */
  refGroup?: string | null;
};

export type AttachmentInput = {
  storageKey: string;
  fileName: string;
  contentType?: string | null;
  sizeBytes?: number | null;
};

export type DocumentWrite = {
  formKey: string;
  title: string;
  body: string;
  amount?: number | null;
  urgency: string;
  securityLevel: string;
  formData?: Record<string, unknown> | null;
  lines: LineInput[];
  attachments?: AttachmentInput[];
};

export type DocAttachment = {
  id: string;
  storageKey: string;
  fileName: string;
  contentType: string | null;
  sizeBytes: number | null;
  downloadUrl: string | null;
};

export type DocSummary = {
  id: string;
  docNo: string | null;
  formKey: string | null;
  title: string;
  drafterName: string | null;
  drafterOrg: string | null;
  amount: number | null;
  status: DocStatus;
  urgency: string;
  submittedAt: string | null;
  createdAt: string;
  /** 지금 내가 처리할 차례인가 — 결재 대기함에 서는 그 문서다. */
  myTurn: boolean;
  /** 내가 열어 봤는가(내가 쓴 글은 늘 참). 내 차례인데 안 봤으면 「N」이 붙는다. */
  read: boolean;
  /** 내 결재·합의줄이 더는 대기가 아닌가 — 내 손으로 끝낸 문서. */
  handled: boolean;
};

export type DocLine = {
  seq: number;
  lineType: LineType;
  approverUserId: string | null;
  approverName: string | null;
  approverPosition: string | null;
  approverOrg: string | null;
  isFinal: boolean;
  /** 숨김 참조. 서버가 기안자·본인 외에는 이 줄을 아예 빼고 내려준다. */
  hidden?: boolean;
  /** 참조 묶음 이름(V396). [부서 전체]로 들어온 참조만 있다. */
  refGroup?: string | null;
  decision: LineDecision;
  actedByName: string | null;
  decidedAt: string | null;
  opinion: string | null;
  postConfirmedAt: string | null;
  /** 결재란에 찍힌 서명 그림 — 누른 순간 복사된 것이다. 미처리·미등록이면 null(V328). */
  signatureImage: string | null;
};

export type DocDetail = {
  id: string;
  docNo: string | null;
  formKey: string | null;
  requestType: string;
  title: string;
  body: string | null;
  formData: string | null;
  amount: number | null;
  drafterUserId: string | null;
  drafterName: string | null;
  drafterOrg: string | null;
  /** 기안(담당) 칸의 서명 그림 — 상신하는 순간 복사된 것이다(V328). */
  drafterSignatureImage: string | null;
  urgency: string;
  securityLevel: string;
  status: DocStatus;
  currentSeq: number;
  resultNote: string | null;
  submittedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  canAct: boolean;
  lines: DocLine[];
  logs: Array<{ event: string; actorName: string | null; note: string | null; createdAt: string }>;
  attachments: DocAttachment[];
};

/**
 * 첨부(참조문서)를 스토리지에 올리고 저장용 메타를 돌려준다. 급여명세서와 같은 규칙 —
 * presigned PUT URL 을 받아 파일을 직접 올리고, 오브젝트 키만 저장 요청에 싣는다.
 * 스토리지가 꺼져 있으면(로컬·미설정) presign 단계에서 STORAGE_DISABLED 로 막힌다.
 */
export async function uploadApprovalFile(file: File): Promise<AttachmentInput> {
  const { key, url } = await apiFetch<{ key: string; url: string }>('/v1/approvals/uploads', {
    method: 'POST',
    body: { fileName: file.name },
  });
  const put = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!put.ok) throw new Error('파일 업로드에 실패했습니다.');
  return { storageKey: key, fileName: file.name, contentType: file.type || null, sizeBytes: file.size };
}

const BOX_KEY = ['approval', 'box'] as const;

export type LinePreview = {
  lines: LineInput[];
  finalRule: {
    finalRole: string;
    /** 값이 있으면 전결자는 그 조직의 장이다(잡자재 = 외주구매팀장). 「팀장」 의 뜻이 기안자 부서장에서 이쪽으로 바뀐다. */
    finalDept: string | null;
    /** 값이 있으면 전결자는 그 담당 역할을 지목받은 사람이다(저렴한 품의 = 경비 담당). */
    finalDutyRole: string | null;
    /** 그 담당을 지목받은 사람이 없어 <b>문서를 끝낼 사람이 결재선에 없다</b> — 반드시 알려야 한다. */
    finalDutyMissing: boolean;
    /** 값이 있으면 전결자는 그 직책을 맡은 사람이다(현장대리인·본부장). */
    finalDutyTitle: string | null;
    /** 규정이 세우라고 적어 둔 자리인데 사람이 없어 못 세운 것들(표시 이름). 조용히 빠지면 아무도 모른다. */
    missingTargets: string[];
    minAmount: number | null;
    maxAmount: number | null;
    satisfiedByAuto: boolean;
    autoApprove: boolean;
  } | null;
};

/** 자동 결재선 미리보기 — (서식, 금액)으로 기본 결재선과 최종 전결 직급을 받는다. 버튼 클릭 시 호출. */
/** `recipient` 는 기안 화면이 고른 수신 부서·현장(협조전) — 규정의 「수신 부서의 장」 줄이 그 부서에서 선다. */
export function fetchLinePreview(formKey: string, amount: number | null, recipient?: string | null): Promise<LinePreview> {
  const q = new URLSearchParams({ formKey: formKey || 'FREE' });
  if (amount != null) q.set('amount', String(amount));
  if (recipient) q.set('recipient', recipient);
  return apiFetch<LinePreview>(`/v1/approvals/line-preview?${q.toString()}`);
}

// `enabled` — 사이드바 검색은 검색어를 친 뒤에만 서식을 받는다(전자결재가 닫힌 사람은 받지 않는다).
export function useApprovalForms({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: ['approval', 'forms'], queryFn: () => apiFetch<ApprovalForm[]>('/v1/approvals/forms'), enabled });
}

export function useOrgTree() {
  return useQuery({ queryKey: ['approval', 'org-tree'], queryFn: () => apiFetch<OrgUnit[]>('/v1/approvals/org-tree') });
}

/**
 * 기안 화면이 <b>미리 받아 두는 문서번호</b> (사용자 지시 2026-09-15).
 *
 * <p>채번은 상신 때 서버가 한다 — 이 값은 같은 규칙으로 지은 다음 번호라 실제 번호와 하나
 * 어긋날 수 있다. 캐시를 오래 붙들지 않는 이유가 그것이다: 기안 화면을 다시 열 때는 그 사이
 * 올라간 문서까지 센 번호를 보는 편이 맞다.
 */
export function useNextDocNo() {
  return useQuery({
    queryKey: ['approval', 'next-doc-no'],
    queryFn: () => apiFetch<string>('/v1/approvals/next-doc-no'),
    staleTime: 0,
  });
}

/**
 * 결재 대기 건수 — 탭 배지·홈 위젯용.
 *
 * `enabled` 를 받는 이유는 홈이다. 홈은 전 직원이 여는 화면이라 전자결재를 볼 수 없는 계정도 지나가는데,
 * 그때마다 403 을 한 번씩 받아 낼 이유가 없다. 세는 일이라 목록(`/inbox`)을 받아 길이를 재지 않는다 —
 * 홈 진입마다 결재 문서 전문을 실어 나르게 된다.
 */
export function useInboxCount(enabled = true) {
  return useQuery({
    queryKey: [...BOX_KEY, 'count'],
    queryFn: () => apiFetch<number>('/v1/approvals/inbox-count'),
    enabled,
  });
}

/**
 * 결재함 한 함의 목록.
 *
 * <p>{@code enabled} 를 받는 이유는 협조전 탭이다 — 그 탭은 결재함이 아니라 대장이라, 켜 두면
 * 화면에 없는 목록을 실어 나른다. 함을 고르는 값과 부를지 여부는 <b>다른 물음</b>이라 인자를 나눈다.
 */
export function useApprovalBox(box: InboxBox, enabled = true) {
  return useQuery({
    queryKey: [...BOX_KEY, box],
    queryFn: () => apiFetch<DocSummary[]>(`/v1/approvals/${box}`),
    enabled,
  });
}

/**
 * 내가 최근에 쓴 서식 — 상신함과 임시저장을 합쳐 최근 순으로, 서식이 겹치지 않게 (사용자 지시
 * 2026-10-06). 몇 개를 세울지·고를 수 있는지는 {@code groupForms} 가 거른다. 두 함은 결재함 탭
 * 건수와 같은 캐시 키라 결재함에서는 다시 받지 않는다.
 */
export function useRecentFormKeys(): string[] {
  const submitted = useApprovalBox('submitted');
  const drafts = useApprovalBox('drafts');
  return useMemo(() => {
    const docs = [...(submitted.data ?? []), ...(drafts.data ?? [])];
    const at = (doc: DocSummary) => doc.submittedAt ?? doc.createdAt;
    docs.sort((a, b) => at(b).localeCompare(at(a)));
    const keys: string[] = [];
    for (const doc of docs) {
      if (doc.formKey && !keys.includes(doc.formKey)) keys.push(doc.formKey);
    }
    return keys;
  }, [submitted.data, drafts.data]);
}

/**
 * 탭마다 붙일 건수 — 결재함 목록을 그대로 세어 쓴다. 키가 {@link useApprovalBox} 와 같아서
 * 지금 보고 있는 탭은 다시 부르지 않고 캐시를 나눠 쓴다.
 */
export function useApprovalBoxCounts(boxes: readonly InboxBox[]) {
  return useQueries({
    queries: boxes.map((box) => ({
      queryKey: [...BOX_KEY, box],
      queryFn: () => apiFetch<DocSummary[]>(`/v1/approvals/${box}`),
      select: (rows: DocSummary[]) => rows.length,
    })),
  });
}

/**
 * 서식 대장 한 줄 — 협조전 대장의 표가 이 모양이다.
 *
 * <p>「수신」이 여기 없는 것은 빠뜨린 것이 아니다. 그 값은 {@link formData} 안에 있고, 어느 칸이
 * 수신인지는 <b>서식이 안다</b>({@code formSchema.recipientKey}). 서버가 오려 내 보내면 그 규칙이
 * 두 곳에 생긴다(백엔드 {@code LedgerRowResponse} 주석과 짝).
 */
export type LedgerRow = {
  id: string;
  docNo: string | null;
  title: string;
  drafterName: string | null;
  drafterOrg: string | null;
  status: DocStatus;
  urgency: string;
  submittedAt: string | null;
  completedAt: string | null;
  formData: string | null;
  /** 협조 «처리» 완료 시각(V262). 결재 완료와 다른 사건 — 비어 있고 결재는 끝났으면 처리중이다. */
  handledAt: string | null;
};

/**
 * 협조전 대장 — {@code history} 가 거짓이면 「신청」(도는 중), 참이면 「내역」(끝난 것).
 *
 * <p>결재함(`BOX_KEY`)과 <b>키를 나눈다</b>. 범위가 달라서다 — 결재함은 내가 걸린 문서이고 대장은
 * 내 소속이 주고받은 문서다. 같은 키를 쓰면 결재 한 번에 두 화면이 서로의 목록을 덮어쓴다.
 */
export function useFormLedger(formKey: string, history: boolean) {
  return useQuery({
    queryKey: ['approval', 'ledger', formKey, history],
    queryFn: () =>
      apiFetch<LedgerRow[]>(
        `/v1/approvals/ledger?formKey=${encodeURIComponent(formKey)}&history=${history}`,
      ),
  });
}

/**
 * 협조전 처리 결과 — 회신 글·파일과 완료 여부 (V262).
 *
 * <p>{@link completedAt} 이 비면 <b>처리중</b>이다. 상태 문자열을 따로 두지 않는 것은 서버와 같은
 * 규칙이다 — 상태와 시각을 함께 두면 어긋난 줄이 반드시 생긴다.
 *
 * <p>{@link canHandle} 은 <b>서버가 판정한다</b>(받은 부서인가 · 승인이 끝났는가). 프론트의 조건은
 * 단추를 감추는 데만 쓰고, 실제로 막는 것은 서버다.
 */
export type CooperationResult = {
  responseText: string | null;
  respondedName: string | null;
  respondedAt: string | null;
  completedName: string | null;
  completedAt: string | null;
  canHandle: boolean;
};

export function useCooperationResult(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['approval', 'cooperation', id],
    queryFn: () => apiFetch<CooperationResult>(`/v1/approvals/${id}/cooperation`),
    enabled: !!id && enabled,
  });
}

/**
 * 회신 저장 · 완료 · 완료 되돌리기.
 *
 * <p>끝나면 <b>대장도 다시 읽는다.</b> 완료를 누르면 그 줄이 「신청」에서 「내역」으로 옮겨 가는데,
 * 목록을 안 비우면 방금 끝낸 건이 여전히 신청함에 남아 있다.
 */
export function useCooperationActions(id: string) {
  const queryClient = useQueryClient();
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['approval', 'cooperation', id] });
    void queryClient.invalidateQueries({ queryKey: ['approval', 'doc', id] });
    void queryClient.invalidateQueries({ queryKey: ['approval', 'ledger'] });
  };
  return {
    /**
     * 회신 저장. <b>{@code attachments} 를 안 주면 파일은 건드리지 않는다</b> — 파일을 붙일 수 없는
     * 화면(앱)이 글만 고칠 때 웹에서 붙여 둔 파일이 함께 지워지면 안 된다(서버도 같은 규칙이다).
     */
    save: useMutation({
      mutationFn: (body: { responseText: string; attachments?: AttachmentInput[] }) =>
        apiFetch<CooperationResult>(`/v1/approvals/${id}/cooperation`, { method: 'PUT', body }),
      onSuccess: done,
    }),
    complete: useMutation({
      mutationFn: () => apiFetch<CooperationResult>(`/v1/approvals/${id}/cooperation/complete`, { method: 'POST' }),
      onSuccess: done,
    }),
    reopen: useMutation({
      mutationFn: () => apiFetch<CooperationResult>(`/v1/approvals/${id}/cooperation/reopen`, { method: 'POST' }),
      onSuccess: done,
    }),
  };
}

/**
 * 문서 한 건. <b>여는 것이 곧 읽는 것</b>이라 서버가 열람을 남기므로, 받아 온 뒤 결재함 목록을 비운다 —
 * 안 그러면 방금 열어 본 줄에 「N」이 그대로 붙어 있다(사용자 지시 2026-09-15).
 */
export function useApprovalDoc(id: string | null) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['approval', 'doc', id],
    queryFn: async () => {
      const detail = await apiFetch<DocDetail>(`/v1/approvals/${id}`);
      void queryClient.invalidateQueries({ queryKey: BOX_KEY });
      return detail;
    },
    enabled: !!id,
  });
}

export function useSaveDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: DocumentWrite }) =>
      id
        ? apiFetch<DocDetail>(`/v1/approvals/${id}`, { method: 'PUT', body })
        : apiFetch<DocDetail>('/v1/approvals', { method: 'POST', body }),
    onSuccess: () => invalidateBoxes(queryClient),
  });
}

/** 후결 — 대결 처리된 내 결재줄을 원결재자가 사후 확인. */
export function usePostConfirm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, seq }: { id: string; seq: number }) =>
      apiFetch<DocDetail>(`/v1/approvals/${id}/post-confirm`, { method: 'POST', body: { seq } }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['approval', 'doc', variables.id] });
      void queryClient.invalidateQueries({ queryKey: ['approval', 'notifications'] });
    },
  });
}

// ── 인앱 알림함 ────────────────────────────────────────────────────────────────────

export type ApprovalNotification = {
  id: string;
  documentId: string | null;
  type: 'TURN' | 'COMPLETED' | 'REJECTED' | 'DELEGATED' | 'POST_CONFIRM';
  title: string;
  message: string | null;
  read: boolean;
  createdAt: string;
};

const NOTI_KEY = ['approval', 'notifications'] as const;

export function useNotifications() {
  return useQuery({ queryKey: NOTI_KEY, queryFn: () => apiFetch<ApprovalNotification[]>('/v1/approvals/notifications') });
}
export function useUnreadCount() {
  return useQuery({ queryKey: [...NOTI_KEY, 'unread'], queryFn: () => apiFetch<number>('/v1/approvals/notifications/unread-count') });
}
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/approvals/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTI_KEY }),
  });
}
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>('/v1/approvals/notifications/read-all', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTI_KEY }),
  });
}

/** 상신·회수·승인·반려·취소를 한 훅으로. 끝나면 모든 함과 그 문서를 다시 읽는다. */
/** 상신한 뒤 첨부를 더한다 — 기안자만, 결재 완료 전까지(2026-10-26). 빼는 길은 없다. */
export function useAddAttachments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, attachments }: { id: string; attachments: AttachmentInput[] }) =>
      apiFetch<DocDetail>(`/v1/approvals/${id}/attachments`, { method: 'POST', body: attachments }),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({ queryKey: ['approval', 'doc', variables.id] }),
  });
}

export function useDocAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, comment }: { id: string; action: 'submit' | 'recall' | 'approve' | 'reject' | 'cancel'; comment?: string }) => {
      const method = action === 'submit' || action === 'recall' || action === 'cancel' ? 'POST' : 'PUT';
      const body = action === 'approve' || action === 'reject' || action === 'cancel' ? { comment } : undefined;
      return apiFetch<DocDetail>(`/v1/approvals/${id}/${action}`, { method, body });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['approval', 'doc', variables.id] });
      return invalidateBoxes(queryClient);
    },
  });
}

function invalidateBoxes(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: BOX_KEY });
}

// ── 전결규정(관리) ────────────────────────────────────────────────────────────────

/**
 * 규정이 세우는 줄 하나 — 전결 앞줄(결재·합의)이거나 참조.
 *
 * 담는 것은 사람이 아니라 <b>자리</b>다: 담당 역할 코드(`DUTY_ROLE`)이거나 조직 이름(`UNIT_LEAD` —
 * 그 조직의 장). 사람을 박아 두면 그가 휴가만 가도 규정이 멈춘다.
 */
/**
 * 결재선 한 줄이 가리키는 자리.
 *
 * `DRAFTER_LEAD` 는 **기안자의 부서장**이다(V134). 값(`targetValue`)을 보지 않는다 — 사람은
 * 기안자마다 다르고, 이 자리가 말하는 것은 「그 사람의 팀장」 하나뿐이다. 규정에 이 줄이 있으면
 * 서버가 부서장을 전결자 앞에 자동으로 박지 않고 **적힌 순서대로** 세운다.
 *
 * `USER` 는 자리가 아니라 **지정한 직원**이다(V512 · 2026-10-20 지시). 값이 그 사람의 계정 id 다.
 */
export type TargetKind = 'DUTY_ROLE' | 'UNIT_LEAD' | 'DUTY_TITLE' | 'DRAFTER_LEAD' | 'RECIPIENT_LEAD' | 'USER';
export type RuleLine = {
  lineType: 'APPROVAL' | 'AGREEMENT' | 'REFERENCE';
  targetType: TargetKind;
  targetValue: string;
};
export type ApprovalRuleRow = {
  id: string;
  formId: string | null;
  minAmount: number | null;
  maxAmount: number | null;
  finalRole: string;
  finalDept: string | null;
  finalDutyRole: string | null;
  finalDutyTitle: string | null;
  lines: RuleLine[];
  active: boolean;
  autoApprove: boolean;
  note: string | null;
  /** 최종 전결자로 지정한 직원의 계정 id(V512). 재시작 전 서버는 이 칸을 안 준다. */
  finalUserId?: string | null;
};
export type RuleWrite = {
  formKey: string | null;
  minAmount: number | null;
  maxAmount: number | null;
  finalRole: string;
  finalDept: string | null;
  finalDutyRole: string | null;
  finalDutyTitle: string | null;
  /** 보낸 목록이 저장되는 전부다 — 빈 목록은 「없앤다」 는 뜻이다. */
  lines: RuleLine[];
  active: boolean;
  autoApprove: boolean;
  note?: string | null;
  finalUserId: string | null;
};
export type PositionInfo = { name: string; rankOrder: number | null; executive: boolean };

/**
 * 결재선에 세울 수 있는 담당 역할. `assignedCount` 가 0 이면 규정에 적어도 결재선에 아무도 서지 않는다 —
 * 고르는 자리에서 그 사실이 보여야 한다(서버가 지목 역할만 준다).
 */
export type DutyRoleInfo = { code: string; name: string; description: string | null; assignedCount: number };

/**
 * 결재선에 세울 수 있는 <b>직책</b>(`ref_duties`). 전결은 직급이 아니라 자리가 한다 —
 * 과장이 팀장인 팀이 있고 부장이 팀원인 팀이 있다.
 *
 * `scopeKind` 는 그 자리가 어디에 붙는가다(`SITE`·`DEPARTMENT`·`NONE`). 현장에 붙는 자리는
 * 기안자와 <b>같은 현장</b>의 사람을 먼저 찾는다. `holderCount` 가 0 이면 아무도 서지 않는다.
 */
export type DutyTitleInfo = { name: string; scopeKind: string; holderCount: number };

export function useApprovalRules() {
  return useQuery({ queryKey: ['approval', 'rules'], queryFn: () => apiFetch<ApprovalRuleRow[]>('/v1/approvals/rules') });
}
export function usePositions() {
  return useQuery({ queryKey: ['approval', 'positions'], queryFn: () => apiFetch<PositionInfo[]>('/v1/approvals/positions') });
}
export function useDutyRoles() {
  return useQuery({ queryKey: ['approval', 'duty-roles'], queryFn: () => apiFetch<DutyRoleInfo[]>('/v1/approvals/duty-roles') });
}
export function useDutyTitles() {
  return useQuery({ queryKey: ['approval', 'duty-titles'], queryFn: () => apiFetch<DutyTitleInfo[]>('/v1/approvals/duty-titles') });
}
export function useSaveRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: RuleWrite }) =>
      id
        ? apiFetch<ApprovalRuleRow>(`/v1/approvals/rules/${id}`, { method: 'PUT', body })
        : apiFetch<ApprovalRuleRow>('/v1/approvals/rules', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['approval', 'rules'] }),
  });
}
export function useDeleteRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/approvals/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['approval', 'rules'] }),
  });
}

// ── 부서별 업무 전결표(V439) ──────────────────────────────────────────────────────

/** 칸 값. 빈 칸은 '' — 그 자리는 이 업무에 서지 않는다. */
export type DutyMark = '' | '기안' | '검토' | '전결';

export type DutyMatrixDept = { id: string; name: string; parentId: string | null };

export type DutyMatrixRow = {
  deptId: string;
  task: string;
  mainWorker: string | null;
  /**
   * 동명이인 중 고른 사람과 이미 이어져 있던 사람들(V447). 이름 글자로 짝짓는다 — 칸에서 그 이름이 빠지면 버려진다.
   * 나머지 이름은 저장할 때 서버가 찾는다.
   */
  workerPicks: { name: string; personnelId: string }[];
  drafterMark: string | null;
  leadMark: string | null;
  divisionMark: string | null;
  ceoMark: string | null;
  note: string | null;
  cooperation: string | null;
};

export type DutyMatrix = {
  departments: DutyMatrixDept[];
  rows: (Omit<DutyMatrixRow, 'workerPicks'> & { id: string; workers?: DutyMatrixWorker[] })[];
  /** 재직자 — 주 업무자 칸에서 적어 찾는 목록. 서버가 재시작 전이면 없다. */
  people?: DutyMatrixPerson[];
};

export type DutyMatrixPerson = { id: string; name: string; rank?: string | null; deptSite?: string | null };

/**
 * 주 업무자 칸을 나눈 이름 하나(V447 · 「유영진, 홍길동 부장」 이면 둘). 이어진 사람이 있으면 label 이 「유영진 대리 · 기술 1부」,
 * 동명이인이면 candidates 가 둘 이상, 못 찾았으면 둘 다 비어 있다. 서버가 null 칸을 빼고 보낸다.
 */
export type DutyMatrixWorker = {
  name: string;
  personnelId?: string | null;
  label?: string | null;
  candidates?: { id: string; label: string }[];
  /** 사람이 아니라 자리를 적은 것(「공통」·「팀장」·「해당 팀장」·「전 팀」). 잇지 않고, 못 찾은 이름으로도 치지 않는다. */
  role?: boolean;
};

export function useDutyMatrix() {
  return useQuery({ queryKey: ['approval', 'duty-matrix'], queryFn: () => apiFetch<DutyMatrix>('/v1/approvals/duty-matrix') });
}

/** 고른 부서와 그 하위 부서를 통째로 저장한다 — 보낸 줄이 그 범위의 전부다. 줄마다 deptId 로 부서를 적는다. */
export function useSaveDutyMatrix() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deptId, rows }: { deptId: string; rows: DutyMatrixRow[] }) =>
      apiFetch<DutyMatrix>(`/v1/approvals/duty-matrix/${deptId}`, { method: 'PUT', body: { rows } }),
    onSuccess: (data) => queryClient.setQueryData(['approval', 'duty-matrix'], data),
  });
}

// ── 저장한 결재선(내 결재선) ──────────────────────────────────────────────────────

/**
 * 이름 붙여 둔 내 결재선. 연차·업무보고·출장은 올리는 사람이 서로 달라서, 규정이 뽑아 주는 자동 결재선
 * 하나로는 매번 고쳐야 한다. 한 번 고른 줄을 이름으로 남겨 두고 그때마다 불러 쓴다.
 */
export type LineTemplate = {
  id: string;
  name: string;
  /** null = 서식 무관. 있으면 그 서식에서 먼저 보여 준다. */
  formKey: string | null;
  lines: LineInput[];
  updatedAt: string | null;
};
export type LineTemplateWrite = {
  name: string;
  formKey?: string | null;
  lines: LineInput[];
};

const TEMPLATE_KEY = ['approval', 'line-templates'] as const;

export function useLineTemplates() {
  return useQuery({
    queryKey: TEMPLATE_KEY,
    queryFn: () => apiFetch<LineTemplate[]>('/v1/approvals/line-templates'),
  });
}

/** 저장 — 같은 이름이면 서버가 덮어쓴다. */
export function useSaveLineTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LineTemplateWrite) =>
      apiFetch<LineTemplate>('/v1/approvals/line-templates', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TEMPLATE_KEY }),
  });
}

export function useDeleteLineTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/approvals/line-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TEMPLATE_KEY }),
  });
}

// ── 위임(대결) ────────────────────────────────────────────────────────────────────

export type Delegation = {
  id: string;
  delegatorUserId: string | null;
  delegatorName: string | null;
  delegateUserId: string | null;
  delegateName: string | null;
  startDate: string;
  endDate: string;
  formId: string | null;
  active: boolean;
  note: string | null;
};
export type DelegationWrite = {
  delegateUserId: string;
  delegateName?: string | null;
  startDate: string;
  endDate: string;
  formKey?: string | null;
  note?: string | null;
};

export function useDelegations() {
  return useQuery({ queryKey: ['approval', 'delegations'], queryFn: () => apiFetch<Delegation[]>('/v1/approvals/delegations') });
}
export function useCreateDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DelegationWrite) => apiFetch<Delegation>('/v1/approvals/delegations', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['approval', 'delegations'] }),
  });
}
export function useDeleteDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/approvals/delegations/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['approval', 'delegations'] }),
  });
}

// ── 개인 서명 ─────────────────────────────────────────────────────────────────────

/**
 * 결재란에 찍힐 내 서명 (V327 · 사용자 지시 2026-09-22).
 *
 * <p>미리 그려 두는 것이라 <b>결재 흐름과 아무 상태도 나눠 쓰지 않는다</b> — 함 목록을 무효화하지
 * 않는 이유가 그것이다. 아직 안 그린 사람은 {@code image} 가 null 이고, 그것은 오류가 아니라
 * 빈 그림판이다.
 */
export type MySignature = { image: string | null; updatedAt: string | null };

const SIGNATURE_KEY = ['approval', 'signature'] as const;

export function useMySignature() {
  return useQuery({
    queryKey: SIGNATURE_KEY,
    queryFn: () => apiFetch<MySignature>('/v1/approvals/signature'),
  });
}

/** 저장 — 이미 있으면 갈아 끼운다(한 사람에 한 장). 서버가 돌려준 것이 정본이다. */
export function useSaveSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (image: string) =>
      apiFetch<MySignature>('/v1/approvals/signature', { method: 'PUT', body: { image } }),
    onSuccess: (saved) => queryClient.setQueryData(SIGNATURE_KEY, saved),
  });
}

export function useDeleteSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<MySignature>('/v1/approvals/signature', { method: 'DELETE' }),
    onSuccess: (empty) => queryClient.setQueryData(SIGNATURE_KEY, empty),
  });
}
